import React from 'react';
import { cn } from '~/lib/utils';
import styles from './AssistantProse.module.css';
import type { ChatTheme } from './theme';
import { chatThemeClass } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

export interface AssistantProseProps {
  /**
   * `plain` is the bare block the agent speaks in — no ground, no rule, no glyph, `subtle`.
   * `led` is the one exception in the whole design (*Agent corrects itself*): a 16px mark
   * top-aligned to the first line, 2px of optical inset, a 9px gap, and text one tone brighter at
   * `muted`.
   */
  variant?: 'plain' | 'led';
  /**
   * Where on the white ramp the paragraph sits. `subtle` is the default and is the agent's ordinary
   * voice; `default` is `--cv15-text`, full strength.
   *
   * The two exist because not everything drawn in this component is the agent TALKING. A turn also
   * draws work notes, a folded thought's reasoning and a failure's explanation through it, and all
   * of those are a record of what happened rather than the reply — quiet on purpose, and quiet is
   * what makes the reply legible as the reply. Beka, 2026-09-20: "the last output from the agent
   * needs to be full white."
   *
   * So the rule is not "prose is bright", it is: what the agent SAYS is full strength, what the
   * agent DID stays subtle. `DeckThread` is where that call is made, because it is the only place
   * that knows which of the two a given block is.
   *
   * Ignored by `led`, which owns its own tone — see `.body`.
   */
  tone?: 'subtle' | 'default';
  /** The `led` mark — an `ActivityGlyph` or a `DeckIcon`. Ignored when `variant` is `plain`. */
  icon?: React.ReactNode;
  children: React.ReactNode;
  theme?: ChatTheme;
  className?: string;
}

/**
 * The agent's paragraph. **It has no bubble, and that is the design**, not an omission: the user's
 * message is an object placed into the thread and the agent's message is the thread. Two bubbles
 * would make the sides symmetric, and the conversation is not.
 *
 * Its metrics — Inter 400 **14/22** −0.01em — are the only body copy in the chat that is not 20 or
 * 21, because it is the only run that routinely wraps past three lines with nothing behind it.
 * `AnswerHeading` shares those exact metrics so a heading and the paragraph under it sit on one
 * rhythm; only weight and colour separate the two.
 */
export function AssistantProse({
  variant = 'plain',
  tone = 'subtle',
  icon,
  children,
  theme: themeProp,
  className,
}: AssistantProseProps) {
  const theme = useV15Theme(themeProp);
  // Before `className`, so a caller's own class still has the last word over the tone.
  const root = cn(chatThemeClass(theme), styles.root, tone === 'default' && styles.full, className);

  if (variant === 'led') {
    return (
      <div className={cn(root, styles.led)}>
        {icon ? <span className={styles.glyph}>{icon}</span> : null}
        <div className={styles.body}>{children}</div>
      </div>
    );
  }

  return <div className={root}>{children}</div>;
}
